import { CoverageEventType } from "@graphql/api";

import { FormProvider, useForm } from "react-hook-form";
import Typography from "@mui/material/Typography";
import {
  CancelButton,
  Form,
  FormSelectField,
  SubmitButton,
} from "@covertree/ui";
import Grid from "@mui/material/Grid";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { useAppSelector } from "@redux/hooks/useAppSelector";

import { EventTypeFormData, eventTypeValidationSchema } from "./validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { moveBackInPathname } from "@utils";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BASE_RESIDENTS_PATH } from "@routing/Residents";
import { ADD_EVENT_ROUTES } from "@routing/Residents/ProofOfCoverage";
import {
  selectEventType,
  selectPreEventCoverage,
  setEventType,
} from "@redux/event/eventSlice";
import {
  addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent,
  selectExternalCoverageData,
} from "@redux/coverages/coveragesSlice";

const EVENT_TYPES = [
  {
    label: "Endorsement",
    value: CoverageEventType.Endorsement,
  },
  {
    label: "Cancellation",
    value: CoverageEventType.Cancellation,
  },
  {
    label: "Reinstatement",
    value: CoverageEventType.Reinstatement,
  },
  {
    label: "Renewal",
    value: CoverageEventType.Renewal,
  },
  {
    label: "Non-Renewal",
    value: CoverageEventType.NonRenewal,
  },
];

export function EventType() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const { residentId } = useParams();

  const eventType = useAppSelector(selectEventType);
  const externalPolicyData = useAppSelector(selectExternalCoverageData);
  const preEventCoverage = useAppSelector(selectPreEventCoverage);

  useEffect(() => {
    if (!externalPolicyData.policyId) {
      navigate({
        pathname: moveBackInPathname(location.pathname, 4),
        search: createSearchParams(searchParams).toString(),
      });
    }
  }, [externalPolicyData, preEventCoverage]);

  const onSubmit = (formData: EventTypeFormData) => {
    if (eventType && formData.eventType !== eventType) {
      dispatch(
        addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent(
          preEventCoverage
        )
      );
    }
    dispatch(setEventType(formData));

    navigate({
      pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_DETAILS}`,
      search: createSearchParams(searchParams).toString(),
    });
  };

  const onClose = useCallback(() => {
    if (residentId) {
      navigate({
        pathname: moveBackInPathname(location.pathname, 4),
        search: createSearchParams(searchParams).toString(),
      });
    } else {
      navigate(BASE_RESIDENTS_PATH);
    }
  }, [navigate, residentId, searchParams]);

  const form = useForm({
    defaultValues: {
      eventType,
    },
    mode: "all",
    resolver: yupResolver(eventTypeValidationSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
  } = form;

  const selectedEventType = watch("eventType");

  return (
    <>
      <Typography
        variant="h3"
        mb={4}
        pl={1}
        sx={{ backgroundColor: "background.default" }}>
        Policy Event Type
      </Typography>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormSelectField
                id="select-event-type"
                control={control}
                options={EVENT_TYPES}
                label="Select your policy event type"
                placeholder="Select an event type"
                name="eventType"
              />
            </Grid>
            <Grid item xs={12} container gap={2}>
              <SubmitButton
                width={200}
                loading={isSubmitting}
                disabled={!selectedEventType || isSubmitting || !isValid}>
                Continue
              </SubmitButton>
              <CancelButton onCancel={onClose} width={200} />
            </Grid>
          </Grid>
        </Form>
      </FormProvider>
    </>
  );
}
